import { Reveal } from "@components/ui";

const emergencySteps = [
  {
    number: "01",
    title: "Stay Calm & Don't Ignite",
    description: "Do not use electrical switches, lighters, or open flames.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M18.36 6.64A9 9 0 005.64 19.36 9 9 0 0018.36 6.64z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    number: "02",
    title: "Shut Off Gas Supply",
    description: "Turn off your gas valve if it is safely accessible.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: "03",
    title: "Evacuate Immediately",
    description: "Leave the area and move everyone to a safe distance.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: "04",
    title: "Call Emergency Line",
    description: "Call 800-GASIS (42747) and select Emergency Service.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: "05",
    title: "Wait for Our Team",
    description: "Certified technicians will arrive within 60 minutes (Dubai / Sharjah zones).",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export default function EmergencyInfoSection() {
  return (
    <section className="w-full bg-[#f8f8f8] py-12 md:py-16 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        {/* Header */}
        <Reveal direction="up" className="text-center mb-10 md:mb-14 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#fff0ed] rounded-full px-4 py-1.5 mb-4 md:mb-5">
            <span className="w-2 h-2 rounded-full bg-[#f03813] animate-pulse" />
            <span className="text-[13px] md:text-[14px] font-semibold text-[#f03813] font-albert-sans tracking-wide uppercase">
              Emergency Protocol
            </span>
          </div>
          <h2 className="text-[28px] md:text-[44px] lg:text-[54px] font-semibold text-[#122d63] tracking-[-0.04em] font-albert-sans leading-tight">
            In Case of Gas Leak or Emergency
          </h2>
          <p className="text-[16px] md:text-[20px] lg:text-[24px] text-[#62636c] font-albert-sans mt-3 md:mt-4 tracking-[-0.02em] max-w-[700px] mx-auto">
            If you smell gas or suspect a leak, follow these safety steps immediately:
          </p>
        </Reveal>

        {/* Steps — numbered cards with left accent */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {emergencySteps.map((step, index) => (
            <Reveal key={step.number} direction="up" delay={index * 80} className="h-full">
              <div className="relative h-full p-6 md:p-7 rounded-[20px] border border-[rgba(98,99,108,0.12)] bg-white hover:shadow-lg hover:shadow-[#f03813]/5 transition-all duration-300 group overflow-hidden">
                {/* Red left accent bar */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#f03813] to-[#f03813]/40 rounded-l-[20px]" />

                {/* Large watermark number */}
                <span className="absolute top-3 right-4 text-[72px] md:text-[80px] font-bold text-[#f03813]/[0.04] font-albert-sans leading-none select-none pointer-events-none">
                  {step.number}
                </span>

                {/* Icon + Number row */}
                <div className="relative flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-[10px] bg-[#fff0ed] flex items-center justify-center shrink-0 text-[#f03813] group-hover:bg-[#f03813] group-hover:text-white transition-colors duration-300">
                    {step.icon}
                  </div>
                  <span className="text-[14px] font-bold text-[#f03813]/60 font-albert-sans tracking-wider">
                    STEP {step.number}
                  </span>
                </div>

                {/* Text */}
                <h3 className="relative text-[20px] md:text-[22px] font-semibold text-[#090d2c] font-albert-sans leading-tight mb-2">
                  {step.title}
                </h3>
                <p className="relative text-[15px] md:text-[16px] text-[#62636c] font-albert-sans leading-relaxed">
                  {step.description}
                </p>
              </div>
            </Reveal>
          ))}

          {/* Emergency hotline card — fills remaining grid slot */}
          <Reveal direction="up" delay={emergencySteps.length * 80} className="h-full">
            <a
              href="tel:80042747"
              className="relative h-full flex flex-col items-center justify-center p-6 md:p-7 rounded-[20px] bg-gradient-to-br from-[#f03813] to-[#d42f0f] text-white overflow-hidden group hover:shadow-xl hover:shadow-[#f03813]/25 transition-all duration-300"
            >
              {/* Decorative ring */}
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full border-[3px] border-white/10" />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full border-[3px] border-white/10" />

              <div className="relative text-center">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7">
                    <path
                      d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="text-[14px] font-medium text-white/80 font-albert-sans mb-1">
                  24/7 Emergency Hotline
                </p>
                <p className="text-[24px] md:text-[28px] font-bold font-albert-sans leading-tight">
                  800-GASIS (42747)
                </p>
                <p className="text-[13px] text-white/60 font-albert-sans mt-2">
                  Tap to call now
                </p>
              </div>
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
